import { cn } from "@/lib/utils";
import { Link, useLocation } from "@tanstack/react-router";
import { ChevronDown, ChevronRight } from "lucide-react";
import { useState } from "react";
import {
  filterNavItems,
  type DomainSidebarConfig,
  type DomainSidebarItem,
} from "@/modules/navigation";
import { ScrollArea } from "@/components/ui/scroll-area";
import { SidebarUser } from "./sidebar-user";
import { useCurrentOperator } from "@/hooks/use-operator";
import { useMethodNavItems } from "@/features/analytics/methods/use-method-nav-items";

const DOMAIN_SIDEBAR_WIDTH = 160;

interface DomainSidebarProps {
  /** Domain sidebar configuration */
  config: DomainSidebarConfig | null;
}

/**
 * Domain sidebar - shown alongside the global rail in browse mode.
 * Displays domain-specific navigation items organized by content/schema split.
 */
export function DomainSidebar({ config }: DomainSidebarProps) {
  const { data: operator } = useCurrentOperator();
  const capabilities = operator?.capabilities ?? [];
  const methodItems = useMethodNavItems();

  if (!config) {
    // No domain sidebar for global pages (home)
    return null;
  }

  const items = filterNavItems(config.items, capabilities).flatMap((item) =>
    item.type === "methods" ? methodItems : [item],
  );

  return (
    <div
      className="flex flex-col h-svh bg-background border-r border-olive-6 shrink-0"
      style={{ width: DOMAIN_SIDEBAR_WIDTH }}
    >
      {/* Header */}
      <div className="px-3 py-2.5 border-b border-olive-6">
        <span className="font-medium text-sm">{config.label}</span>
      </div>

      {/* Navigation */}
      <ScrollArea className="flex-1">
        <nav className="py-1">
          {items.map((item, index) => (
            <DomainNavItem
              key={"id" in item ? item.id : `${item.type}-${index}`}
              item={item}
            />
          ))}
        </nav>
      </ScrollArea>

      {/* Footer */}
      <div className="border-t border-olive-6 px-2 py-3">
        <SidebarUser />
      </div>
    </div>
  );
}

function DomainNavItem({
  item,
  indent,
}: {
  item: DomainSidebarItem;
  indent?: boolean;
}) {
  if (item.type === "divider") {
    return <DomainDivider />;
  }
  if (item.type === "section") {
    return <DomainSection item={item} />;
  }
  if (item.type === "link") {
    return <DomainLink item={item} indent={indent} />;
  }
  return null;
}

function DomainDivider() {
  return (
    <div className="py-2 px-3">
      <div className="border-t border-olive-6" />
    </div>
  );
}

function DomainLink({
  item,
  indent,
}: {
  item: Extract<DomainSidebarItem, { type: "link" }>;
  indent?: boolean;
}) {
  const location = useLocation();

  const isRootLink = item.href.split("/").filter(Boolean).length <= 1;
  const isActive = isRootLink
    ? location.pathname === item.href
    : location.pathname === item.href ||
      location.pathname.startsWith(item.href + "/");

  return (
    <Link
      to={item.href}
      className={cn(
        "flex items-center py-1.5 text-sm transition-colors",
        indent ? "pl-5 pr-3" : "px-3",
        isActive
          ? "bg-primary/10 text-primary font-medium"
          : "text-muted-foreground hover:text-foreground hover:bg-muted",
      )}
    >
      <span className="truncate">{item.label}</span>
    </Link>
  );
}

function DomainSection({
  item,
}: {
  item: Extract<DomainSidebarItem, { type: "section" }>;
}) {
  const location = useLocation();
  const [isExpanded, setIsExpanded] = useState(() => {
    if (item.defaultExpanded) return true;
    // Auto-expand if any child is active
    return item.children.some(
      (child) =>
        child.type === "link" &&
        (location.pathname === child.href ||
          location.pathname.startsWith(child.href + "/")),
    );
  });

  const hasActiveChild = item.children.some(
    (child) =>
      child.type === "link" &&
      (location.pathname === child.href ||
        location.pathname.startsWith(child.href + "/")),
  );

  return (
    <div>
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className={cn(
          "w-full flex items-center gap-1 px-3 pt-3 pb-1 text-xs uppercase tracking-wider transition-colors",
          hasActiveChild
            ? "text-foreground font-semibold"
            : "text-muted-foreground hover:text-foreground",
        )}
      >
        <span className="truncate">{item.label}</span>
        {isExpanded ? (
          <ChevronDown className="w-3 h-3 shrink-0 ml-auto" />
        ) : (
          <ChevronRight className="w-3 h-3 shrink-0 ml-auto" />
        )}
      </button>
      {isExpanded && (
        <div>
          {item.children.map((child, index) => (
            <DomainNavItem
              key={"id" in child ? child.id : `${child.type}-${index}`}
              item={child}
              indent
            />
          ))}
        </div>
      )}
    </div>
  );
}
